import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { ArrowRight, CheckCircle2, CircleDot, Clock4, MessageSquare } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { INITIAL_DEALS, type Deal, type DealStage } from '../lib/operatorWorkspace'
import { cn } from '../lib/utils'

const STAGES: Array<{ id: DealStage; label: string; short: string }> = [
  { id: 'new', label: 'New Lead', short: 'New' },
  { id: 'contacted', label: 'Contacted', short: 'Call' },
  { id: 'interested', label: 'Interested', short: 'Interest' },
  { id: 'negotiating', label: 'Negotiating', short: 'Negotiate' },
  { id: 'offer', label: 'Offer Sent', short: 'Offer' },
  { id: 'contract', label: 'Under Contract', short: 'Contract' },
  { id: 'closed', label: 'Closed', short: 'Closed' },
]

const STAGE_STYLE: Record<DealStage, string> = {
  new: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
  contacted: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-800',
  interested: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/30 dark:text-violet-300 dark:border-violet-800',
  negotiating: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800',
  offer: 'bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-950/30 dark:text-cyan-300 dark:border-cyan-800',
  contract: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800',
  closed: 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950/50 dark:text-emerald-200 dark:border-emerald-700',
}

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n)

function nextStage(stage: DealStage): DealStage {
  const idx = STAGES.findIndex((s) => s.id === stage)
  if (idx < 0 || idx === STAGES.length - 1) {
    return stage
  }
  return STAGES[idx + 1].id
}

type DealPipelineModuleProps = {
  deals?: Deal[]
  onDealsChange?: (deals: Deal[]) => void
}

export default function DealPipelineModule({ deals: controlledDeals, onDealsChange }: DealPipelineModuleProps) {
  const [internalDeals, setInternalDeals] = useState<Deal[]>(INITIAL_DEALS)
  const [selectedDealId, setSelectedDealId] = useState<string>(INITIAL_DEALS[0].id)
  const [notice, setNotice] = useState<string | null>(null)
  const deals = controlledDeals ?? internalDeals

  const updateDeals = (next: Deal[] | ((prev: Deal[]) => Deal[])) => {
    const resolved = typeof next === 'function' ? next(deals) : next
    if (onDealsChange) {
      onDealsChange(resolved)
      return
    }
    setInternalDeals(resolved)
  }

  const selectedDeal = deals.find((d) => d.id === selectedDealId) ?? deals[0]
  const selectedStageIndex = STAGES.findIndex((s) => s.id === selectedDeal.stage)

  const stageCounts = useMemo(() => {
    return STAGES.map((stage) => ({
      ...stage,
      count: deals.filter((d) => d.stage === stage.id).length,
    }))
  }, [deals])

  const kanbanColumns = useMemo(() => {
    return STAGES.map((stage) => ({
      ...stage,
      cards: deals.filter((d) => d.stage === stage.id),
    }))
  }, [deals])

  const progressPct = Math.round(((selectedStageIndex + 1) / STAGES.length) * 100)

  const advanceSelectedDeal = () => {
    const next = nextStage(selectedDeal.stage)
    if (next === selectedDeal.stage) {
      setNotice('Deal already at final stage.')
      return
    }

    updateDeals((prev) =>
      prev.map((deal) =>
        deal.id === selectedDeal.id
          ? { ...deal, stage: next, probability: Math.min(99, deal.probability + 6), lastAction: `Moved to ${STAGES.find((s) => s.id === next)?.label}` }
          : deal,
      ),
    )
    setNotice(`Moved ${selectedDeal.title} to ${STAGES.find((s) => s.id === next)?.label}.`)
  }

  return (
    <div className="space-y-4" data-testid="deal-pipeline-canvas">
      <div>
        <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100">Deals Pipeline</h1>
        <p className="text-xs text-slate-500 dark:text-slate-400">n8n-style visual workflow for realtor progress tracking.</p>
      </div>

      {notice && (
        <Card className="border-emerald-200 bg-emerald-50 dark:border-emerald-900/60 dark:bg-emerald-950/25">
          <CardContent className="py-2 text-sm text-emerald-800 dark:text-emerald-300">{notice}</CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <Card className="p-3 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Open Deals</p>
          <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">{deals.length}</p>
        </Card>
        <Card className="p-3 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Pipeline Value</p>
          <p className="mt-1 text-2xl font-bold text-violet-700 dark:text-violet-400">{fmt(deals.reduce((s, d) => s + d.value, 0))}</p>
        </Card>
        <Card className="p-3 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Avg Probability</p>
          <p className="mt-1 text-2xl font-bold text-blue-700 dark:text-blue-400">{deals.length > 0 ? Math.round(deals.reduce((s, d) => s + d.probability, 0) / deals.length) : 0}%</p>
        </Card>
        <Card className="p-3 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Selected Progress</p>
          <p className="mt-1 text-2xl font-bold text-emerald-700 dark:text-emerald-400">{progressPct}%</p>
        </Card>
      </div>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Workflow Canvas</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <div className="min-w-[940px]">
            <div className="grid grid-cols-7 items-center gap-2">
              {stageCounts.map((stage, idx) => (
                <div key={stage.id} className="relative">
                  <motion.button
                    whileHover={{ y: -1 }}
                    onClick={() => {
                      const found = deals.find((d) => d.stage === stage.id)
                      if (found) {
                        setSelectedDealId(found.id)
                      }
                    }}
                    className={cn(
                      'w-full rounded-xl border px-2 py-2 text-left transition',
                      STAGE_STYLE[stage.id],
                      selectedDeal.stage === stage.id && 'ring-2 ring-emerald-500/60',
                    )}
                  >
                    <p className="text-[10px] uppercase tracking-wider opacity-80">{stage.short}</p>
                    <p className="text-sm font-semibold leading-tight">{stage.label}</p>
                    <div className="mt-1 flex items-center justify-between text-xs">
                      <span>{stage.count} deals</span>
                      {selectedDeal.stage === stage.id ? <CircleDot className="h-3.5 w-3.5" /> : <Clock4 className="h-3.5 w-3.5 opacity-70" />}
                    </div>
                  </motion.button>
                  {idx < STAGES.length - 1 && <ArrowRight className="absolute -right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />}
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Selected Deal</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-[1fr_auto] md:items-center">
          <div>
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{selectedDeal.title}</p>
            <p className="text-xs text-slate-500">Owner: {selectedDeal.owner} · Value: {fmt(selectedDeal.value)} · Probability: {selectedDeal.probability}%</p>
            <p className="mt-1 text-xs text-slate-500">{selectedDeal.lastAction}</p>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
              <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${progressPct}%` }} />
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => setNotice(`Follow-up note created for ${selectedDeal.owner}.`)}>
              <MessageSquare className="mr-1.5 h-3.5 w-3.5" />Log Follow-up
            </Button>
            <Button size="sm" onClick={advanceSelectedDeal}>
              <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />Advance Stage
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Deals Kanban Board</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <div className="grid min-w-[1080px] grid-cols-7 gap-2">
            {kanbanColumns.map((column) => (
              <div key={column.id} className="rounded-xl border border-slate-200 bg-slate-50 p-2 dark:border-slate-700 dark:bg-slate-900/50">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">{column.short}</p>
                  <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">{column.cards.length}</span>
                </div>
                <div className="space-y-2">
                  {column.cards.map((deal) => (
                    <button
                      key={deal.id}
                      onClick={() => setSelectedDealId(deal.id)}
                      className={cn(
                        'w-full rounded-lg border bg-white p-2 text-left text-xs shadow-sm transition dark:border-slate-700 dark:bg-slate-900',
                        selectedDealId === deal.id ? 'border-emerald-300 dark:border-emerald-800' : 'border-slate-200',
                      )}
                    >
                      <p className="font-semibold text-slate-900 dark:text-slate-100">{deal.title}</p>
                      <p className="mt-0.5 text-slate-500">{deal.owner}</p>
                      <p className="mt-0.5 font-semibold text-slate-700 dark:text-slate-300">{fmt(deal.value)}</p>
                    </button>
                  ))}
                  {column.cards.length === 0 && (
                    <div className="rounded-lg border border-dashed border-slate-300 px-2 py-3 text-center text-[10px] text-slate-500 dark:border-slate-700 dark:text-slate-400">No deals</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Deals List</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {deals.map((deal) => (
            <button
              key={deal.id}
              onClick={() => setSelectedDealId(deal.id)}
              className={cn(
                'w-full rounded-xl border px-3 py-2 text-left transition',
                selectedDealId === deal.id ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/20' : 'border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800/50',
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{deal.title}</p>
                <span className={cn('rounded-full border px-2 py-0.5 text-[10px] font-bold', STAGE_STYLE[deal.stage])}>{STAGES.find((s) => s.id === deal.stage)?.label}</span>
              </div>
              <p className="text-xs text-slate-500">{deal.owner} · {fmt(deal.value)} · {deal.probability}%</p>
            </button>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}
